import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import { confirmar } from "@/components/ConfirmDialog";
/** Reorganizar por região: alunas da turma da professora CEO que têm professora/representante com território no estado delas. */
export const RegionDialog = ({ open, onOpenChange, onDone }) => {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState({});      // student_id -> group_id escolhido ("" = não mover)
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setData(null);
    api.get("/admin/regions/preview").then((r) => {
      setData(r.data);
      const s = {}; r.data.rows.forEach((x) => { s[x.student_id] = x.suggested_group_id || ""; });
      setSel(s);
    }).catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); onOpenChange(false); });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const chosen = Object.entries(sel).filter(([, g]) => g);
  const apply = async () => {
    if (!chosen.length) return;
    if (!(await confirmar(`Mover ${chosen.length} aluna(s) para a turma da professora/representante da região? As matrículas e taxas já lançadas não mudam.`))) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/regions/apply", { source_group_id: data.source.id, moves: chosen.map(([student_id, to_group_id]) => ({ student_id, to_group_id })) });
      toast.success(`${r.data.moved} aluna(s) movida(s).`);
      onOpenChange(false); onDone?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-display">Reorganizar por região</DialogTitle>
          <DialogDescription>
            {data ? <>Turma de origem: <strong>{data.source.name}</strong> ({data.source.students} alunas). Só aparecem alunas cujo estado está no território de uma professora/representante. Nada é movido sem você confirmar.</> : "Procurando alunas..."}
          </DialogDescription>
        </DialogHeader>
        {!data ? <div className="py-10 grid place-items-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div> : data.rows.length === 0 ? (
          <p className="text-sm text-slate-500 py-6">Nenhuma aluna para migrar agora. Confira se cada professora/representante tem os estados de atuação marcados (Professores → pessoa → Turmas).{data.without_state ? ` ${data.without_state} aluna(s) estão sem estado no cadastro.` : ""}</p>
        ) : (
          <div className="max-h-[55vh] overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
            {data.rows.map((r) => (
              <div key={r.student_id} className="p-3 flex items-center gap-3 flex-wrap text-sm">
                <input type="checkbox" className="h-4 w-4" checked={!!sel[r.student_id]} disabled={!r.options.length}
                  onChange={(e) => setSel((p) => ({ ...p, [r.student_id]: e.target.checked ? (r.suggested_group_id || r.options[0].group_id) : "" }))} />
                <div className="flex-1 min-w-[200px]">
                  <div className="font-medium text-slate-800">{r.name}</div>
                  <div className="text-xs text-slate-400">{r.city || "sem cidade"} · {r.state}</div>
                </div>
                {r.options.length === 1 ? (
                  <span className="text-xs text-slate-600">→ {r.options[0].group_name} <span className="text-slate-400">({r.options[0].person_name})</span></span>
                ) : (
                  <select value={sel[r.student_id] || ""} onChange={(e) => setSel((p) => ({ ...p, [r.student_id]: e.target.value }))} className="h-9 rounded-md border border-slate-200 bg-white px-2 text-xs">
                    <option value="">— escolha a turma —</option>
                    {r.options.map((o) => <option key={o.group_id} value={o.group_id}>{o.group_name} ({o.person_name}){o.city_match ? " · mesma cidade" : ""}</option>)}
                  </select>
                )}
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Fechar</Button>
          <Button disabled={busy || !chosen.length} onClick={apply} className="text-white" style={{ background: "#1E9E2A" }}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Mover ${chosen.length} aluna(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
